import {
  createRef,
  cloneElement,
  Fragment,
  Component,
  useState,
  useEffect,
} from 'react';
import Proptypes from 'prop-types';

import {
  BodyScroll,
  runAfterIdle,
  scrollSpeedCalculator,
  onSwipe,
} from '../utils/utils';
import { setCookie, getCookie } from '../../../../utils/cookie';
import { log } from '@hungryroot/o11y';
import styles from './Modal.module.css';

export default class Modal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
    };
    this.modal = createRef();
    this.bodyScroll = new BodyScroll();
    this.addOpenCloseToComponent = this.addOpenCloseToComponent.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (!this.state.visible && props.visible) {
      this.openModal();
    } else if (this.state.visible && !props.visible) {
      this.closeModal();
    }
  }

  onDocumentKeyDown(e) {
    if (e.keyCode === 27 && this.state.visible) {
      this.closeModal();
    }
  }

  componentDidMount() {
    if (this.props.visible) {
      this.openModal();
    }
    // make sure it closes on keydown
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  closeModal() {
    this.setState({ visible: false });
    this.bodyScroll.activate();
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  openModal() {
    this.setState({ visible: true });
    this.bodyScroll.stop();
    if (this.props.onOpen) {
      this.props.onOpen();
    }
  }

  addOpenCloseToComponent(comp) {
    return cloneElement(comp, {
      closeModal: () => this.closeModal(),
      openModal: () => this.openModal(),
    });
  }

  render() {
    let children = this.props.children;
    if (children) {
      children = Array.isArray(children)
        ? children.map(this.addOpenCloseToComponent)
        : this.addOpenCloseToComponent(children);
    }

    let Button = this.props.button
      ? cloneElement(this.props.button, {
          onClick: () => this.openModal(),
        })
      : null;

    return (
      <Fragment>
        {Button}
        {this.state.visible ? (
          <div
            className={`modal-overlay ${styles['modal-overlay']}`}
            onClick={() => {
              if (this.props.noOverlayClick) return;
              this.closeModal();
            }}
          >
            <div
              className={`modal ${styles.modal} ${
                this.props.extraClassName ? this.props.extraClassName : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </div>
          </div>
        ) : null}
      </Fragment>
    );
  }
}

class ModalWithBack extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
    };
    this.modal = createRef();
    this.bodyScroll = new BodyScroll();
    this.addOpenCloseToComponent = this.addOpenCloseToComponent.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
  }

  UNSAFE_componentWillReceiveProps(props) {
    if (!this.state.visible && props.visible) {
      this.openModal();
    }
  }

  onDocumentKeyDown(e) {
    if (e.keyCode === 27 && this.state.visible) {
      this.closeModal();
    }
  }

  componentDidMount() {
    if (this.props.visible) {
      this.openModal();
    }
    // make sure it closes on keydown
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onDocumentKeyDown);
  }

  _closeModal() {
    this.setState({ visible: false });
    this.bodyScroll.activate();
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  closeModal() {
    window.history.back();
  }

  openModal() {
    history.pushState(null, null, window.location.href);
    window.addEventListener(
      'popstate',
      () => {
        this._closeModal();
      },
      {
        once: true,
      }
    );
    this.setState({ visible: true });
    this.bodyScroll.stop();
    if (this.props.onOpen) {
      this.props.onOpen();
    }
  }

  addOpenCloseToComponent(comp) {
    return cloneElement(comp, {
      closeModal: () => this.closeModal(),
      openModal: () => this.openModal(),
    });
  }

  render() {
    let children = this.props.children;
    if (children) {
      children = Array.isArray(children)
        ? children.map(this.addOpenCloseToComponent)
        : this.addOpenCloseToComponent(children);
    }

    let Button = this.props.button
      ? cloneElement(this.props.button, {
          onClick: () => this.openModal(),
        })
      : null;

    return (
      <Fragment>
        {Button}
        {this.state.visible ? (
          <div
            className="modal-overlay"
            onClick={() => {
              if (this.props.noOverlayClick) return;
              this.closeModal();
            }}
          >
            <div
              className={`modal ${
                this.props.extraClassName ? this.props.extraClassName : ''
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {children}
            </div>
          </div>
        ) : null}
      </Fragment>
    );
  }
}

//used by Generic Modal and Instructional modal
export const GenericBaseModal = ({
  closeModal,
  title,
  description,
  action,
  actionText,
  closeText = 'CLOSE',
}) => {
  let actionAndCloseModal = (e) => {
    const actionReturn = action(e);
    if (actionReturn && typeof actionReturn.then === 'function') {
      actionReturn.then(closeModal).catch(console.error);
    } else {
      closeModal();
    }
  };
  return (
    <div className="generic-modal" data-testid="generic-modal">
      <div className="modal-txt-container hr-paragraph">
        <div
          className="hr-title-sm hr-mb-16"
          data-testid="generic-modal-title"
          dangerouslySetInnerHTML={{ __html: title }}
        ></div>
        <div
          className="hr-mb-32"
          dangerouslySetInnerHTML={{ __html: description }}
          data-testid="generic-modal-description"
        ></div>
      </div>
      <div className="modal-btn-container">
        {action && actionText ? (
          <Fragment>
            <div className="hr-btn-hol" onClick={closeModal}>
              {closeText ? closeText : 'CANCEL'}
            </div>
            <div
              data-testid="generic-modal-button-action"
              className="hr-btn-blue"
              onClick={actionAndCloseModal}
            >
              {actionText}
            </div>
          </Fragment>
        ) : (
          <div
            data-testid="generic-modal-button-close"
            className="hr-btn-blue"
            onClick={closeModal}
          >
            {closeText ? closeText : 'CLOSE'}
          </div>
        )}
      </div>
    </div>
  );
};

// This is the modal reused everywhere
export const GenericModal = (props) => {
  let {
    title,
    description,
    action,
    actionText,
    closeText,
    button,
    disabled,
    noOverlayClick,
    visible,
    onClose,
  } = props;

  useEffect(() => {
    if (props.visible === true) {
      log('modals-deprecated', {
        type: title,
        props,
      });
    }
  }, [props]);

  return (
    <Modal
      button={button}
      disabled={disabled}
      visible={visible}
      onClose={onClose}
      noOverlayClick={noOverlayClick}
    >
      <GenericBaseModal
        title={title}
        description={description}
        action={action}
        actionText={actionText}
        closeText={closeText}
      />
    </Modal>
  );
};

GenericModal.propTypes = {
  title: Proptypes.string,
  description: Proptypes.string.isRequired,
  action: Proptypes.func,
  actionText: Proptypes.string,
  button: Proptypes.object,
  openModal: Proptypes.bool,
  closeModa: Proptypes.bool,
  onClose: Proptypes.func,
  onOpen: Proptypes.func,
  visible: Proptypes.bool,
};

const OneTimeModal = (props) => {
  let {
    modalId,
    EducationalComponent,
    slides,
    neverShow,
    generic,
    educational,
    title,
    description,
    action,
    actionText,
    closeText,
    extraClassName,
    onClose = () => {},
    visible = true,
    noOverlayClick,
  } = props;

  if (neverShow) setCookie(modalId);
  if (neverShow || getCookie(modalId)) return null;
  const setCookieOnClose = () => {
    setCookie(modalId);
    onClose();
  };

  const getContent = () => {
    if (generic) {
      return (
        <GenericBaseModal
          title={title}
          description={description}
          action={action}
          actionText={actionText}
          closeText={closeText}
          key={modalId}
        />
      );
    } else if (educational) {
      return <EducationalComponent slides={slides} closeText={closeText} />;
    } else {
      return <Fragment></Fragment>;
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={setCookieOnClose}
      extraClassName={extraClassName}
      noOverlayClick={noOverlayClick}
    >
      {getContent()}
    </Modal>
  );
};

class SlidesModalBody extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slide: 0,
    };
    this.slidesModalElement = createRef();
  }

  componentDidMount() {
    const onSwipeFunc = () => {
      this.state.slide === this.props.slides.length - 1
        ? this.props.closeModal()
        : this.gotoNextSlide();
    };
    onSwipe(this.slidesModalElement.current, {
      right: onSwipeFunc,
      up: onSwipeFunc,
      down: onSwipeFunc,
    });
  }

  gotoNextSlide() {
    this.setState({ slide: this.state.slide + 1 });
  }

  render() {
    let { slides, closeModal, closeText } = this.props;
    const currentSlide = slides[this.state.slide];
    const actionFunc =
      this.state.slide === slides.length - 1
        ? closeModal
        : () => this.gotoNextSlide();
    const btnText =
      this.state.slide === slides.length - 1 && closeText ? closeText : 'Next';
    return (
      <div className={'slides-modal-body'} ref={this.slidesModalElement}>
        <img
          className={`hr-logo ${styles['hr-mobile']} modal-img hr-mb-24`}
          src={`/public/img/half_dish_logo.svg`}
          alt="Hungry Root Logo"
        />
        <img
          alt=""
          className={`${styles['hr-mobile']} modal-img hr-mb-24`}
          src={`${currentSlide['mobileImgPath']}`}
        />
        <img
          alt=""
          className={`${styles['hr-desktop']} modal-img hr-mb-24`}
          src={`${currentSlide['desktopImgPath']}`}
        />
        <div className={'slides-modal-lower-half'}>
          <div
            data-testid="slides-modal-body-title"
            className={'title hr-mb-16'}
            dangerouslySetInnerHTML={{ __html: currentSlide['title'] }}
          ></div>
          <div
            data-testid="slides-modal-body-subtext"
            className={'hr-subtitle-lg'}
          >
            {currentSlide['subtext']}
          </div>
          <div className="dots-container">
            {slides.map((_, i) => {
              return (
                <div
                  key={i}
                  className={`dot${i === this.state.slide ? ' full' : ''}`}
                ></div>
              );
            })}
          </div>
          <div
            id={`slide-cta-${this.state.slide}`}
            onClick={() => actionFunc()}
            className={'hr-btn-blue hr-max'}
          >
            {btnText}
          </div>
        </div>
      </div>
    );
  }
}

OneTimeModal.propTypes = {
  modalId: Proptypes.string.isRequired,
  neverShow: Proptypes.bool,
  generic: Proptypes.bool,
  title: Proptypes.string,
  description: Proptypes.string,
  action: Proptypes.func,
  actionText: Proptypes.string,
};
